// ===========================================
// konakla - Herkese açık sorgu alan listeleri
// ===========================================
//
// NEDEN VAR (K-2): ilan sorgularının çoğu ev sahibi profilini
// `owner:profiles!listings_owner_id_fkey(*)` ile çekiyordu — yani `profiles`
// tablosunun **27 kolonunun tamamını**. Bu yanıt herkese açık uçlardan
// dönüyordu ve `cache-control: public` ile CDN'e de girebiliyordu. Canlıda
// ölçüldü: kimliksiz tek bir istek `paytr_card_token_encrypted`,
// `paytr_user_token_encrypted`, `revenuecat_user_id`, `role` ve sekiz
// `subscription_*` alanını döndürüyordu.
//
// ⚠️ BU BİR VERİ SİLME DEĞİL. Kolonlar veritabanında aynen duruyor; yalnızca
// herkese açık yanıtlara KONULMUYOR.
//
// ⚠️ VE BU TEK BAŞINA K-2'Yİ KAPATMAZ. `profiles` üzerindeki RLS SELECT
// politikası hâlâ `USING (true)` ve anon anahtar tasarımı gereği tarayıcıda.
// Yani saldırgan bizim API'mizi hiç kullanmadan doğrudan PostgREST'e sorabilir
// (ölçüldü: `/rest/v1/profiles?select=*` → 27 kolon). Asıl kapatma RLS
// katmanında yapılmalı. Buradaki liste, kaza eseri sızmayı ve PII'nin CDN'e
// yazılmasını durduran DERİNLEMESİNE SAVUNMA katmanıdır.

import type { Profile } from "@/types/database";

/**
 * Misafire gösterilen ev sahibi alanları.
 *
 * `phone`, `whatsapp` ve `email` BİLEREK burada: konakla komisyonsuz ve
 * doğrudan iletişim modeliyle çalışıyor, misafirin ev sahibine ulaşması
 * ürünün kendisi. Buraya `subscription_*`, `paytr_*`, `revenuecat_user_id`
 * veya `role` EKLEME.
 */
export const PUBLIC_OWNER_FIELDS =
  "id, name, surname, profile_image, phone, whatsapp, email, website, description";

/** `listings` sorgularında kullanılacak hazır embed ifadesi. */
export const PUBLIC_OWNER_EMBED = `owner:profiles!listings_owner_id_fkey(${PUBLIC_OWNER_FIELDS})`;

// ===========================================
// İlanın KENDİ kolonları
// ===========================================
//
// NEDEN VAR: Yukarıdaki düzeltme yalnızca ev sahibi EMBED'ini daralttı;
// ilanın kendi kolonları `select("*")` ile dönmeye devam ediyordu. Canlıda
// ölçüldü (2026-08-22) — kimliksiz `GET /api/listings` şunları veriyordu:
//
//   ical_url  →  https://www.airbnb.com/calendar/ical/<id>.ics?t=<GİZLİ TOKEN>
//
// O adres ev sahibinin Airbnb hesabının TAKVİM BESLEMESİ. `?t=` tahmin
// edilemez olduğu için gizlidir; eline geçen herkes o ev sahibinin tüm Airbnb
// doluluk takvimini okuyabilir. Üçüncü bir platformdaki özel veri.
//
// Ayrıca her ilanda `moderated_by` (admin'in kullanıcı kimliği), `moderated_at`
// ve `tourism_license_document` (özel bucket'taki belge yolu) dönüyordu.
// `admin_notes` ile `rejection_reason` bugün boş — ama kolon döndüğü için
// admin bir not yazdığı AN herkese açık olurdu.
//
// ⚠️ BU BİR VERİ SİLME DEĞİL. Kolonlar veritabanında duruyor; yalnızca
// herkese açık yanıtlara konulmuyor.
//
// ⚠️ VE TEK BAŞINA YETMEZ — K-2 ile birebir aynı durum: `listings` RLS'i
// aktif ilanları anon'a açıyor ve kolon kısıtı yok, yani saldırgan bizim
// API'mizi hiç kullanmadan doğrudan PostgREST'e sorabilir (ölçüldü:
// `/rest/v1/listings?select=ical_url` → dolu değer döndü). Asıl kapatma DB
// katmanında: kolon yetkileri ya da herkese açık bir view.

/**
 * Herkese açık ilan sorgularında dönen kolonlar.
 *
 * DIŞARIDA BIRAKILANLAR ve sebepleri:
 *   ical_url, ical_last_sync   → ev sahibinin gizli takvim beslemesi
 *   admin_notes                → yönetim içi not
 *   rejection_reason           → moderasyon gerekçesi (ev sahibine özel)
 *   moderated_by, moderated_at → hangi admin, ne zaman
 *   auto_deactivated_at        → abonelik iç durumu
 *   tourism_license_document   → özel bucket'taki belge yolu
 *   featured_until             → Davet Programı ödül penceresi (025). Misafir bir
 *                                ilanın NEDEN üstte olduğunu görmemeli; sorgu bunu
 *                                sunucuda filtreler ve sıralar, yanıta KOYMAZ.
 *
 * `tourism_license_number` BİLEREK burada: T.C. Kültür ve Turizm Bakanlığı
 * belge numarası ilan sayfasında zaten gösteriliyor (app/listings/[slug]).
 * Belgenin DOSYA YOLU ile NUMARASI farklı şeyler — numara kamuya açık bilgi.
 */
// ⚠️ Bu sabit BİLEREK düz bir şablon dizesi — dizi + `.join()` DEĞİL.
// supabase-js'in tip çıkarımı `select()`'e verilen dizenin LİTERAL tip olmasını
// gerektiriyor; `.join()` sonucu `string`'e genişler ve dönen satır tipi
// `GenericStringError`'a düşer (derleme hatası). Boşluk/satır sonu sorun değil,
// supabase-js kolon dizesindeki boşlukları temizliyor.
export const PUBLIC_LISTING_FIELDS = `
  id, slug, title, description, short_description, property_type, status,
  capacity, bedrooms, bathrooms, beds,
  city, district, neighborhood, address, latitude, longitude,
  cover_image, images,
  nightly_price, currency, minimum_stay, cleaning_fee, pricing_rules,
  amenities, custom_amenities,
  smoking_allowed, pets_allowed, parties_allowed,
  check_in_time, check_out_time, additional_rules, cancellation_policy,
  tourism_license_number,
  owner_id, view_count, contact_click_count, created_at, updated_at
`;

/**
 * Herkese açık ilan sorgusu için hazır select ifadesi:
 * ilanın güvenli kolonları + daraltılmış ev sahibi embed'i.
 */
export const PUBLIC_LISTING_SELECT = `${PUBLIC_LISTING_FIELDS}, ${PUBLIC_OWNER_EMBED}`;

/**
 * Herkese açık yanıtlardan ÇIKARILMASI gereken ilan kolonları.
 *
 * Sorguyu daraltmanın mümkün olmadığı yerde (ör. aynı uç hem sahibe hem
 * herkese hizmet ediyorsa) yanıtı bu listeyle temizle.
 */
export const INTERNAL_LISTING_FIELDS = [
  "ical_url",
  "ical_last_sync",
  "admin_notes",
  "rejection_reason",
  "moderated_by",
  "moderated_at",
  "auto_deactivated_at",
  "tourism_license_document",
  "featured_until",
] as const;

/**
 * Bir ilan nesnesinin herkese açık kopyasını üretir — iç kolonlar çıkarılmış.
 * Girdi DEĞİŞTİRİLMEZ.
 */
export function stripInternalListingFields<T extends Record<string, unknown>>(
  listing: T
): Partial<T> {
  const kopya: Record<string, unknown> = { ...listing };
  for (const alan of INTERNAL_LISTING_FIELDS) delete kopya[alan];
  return kopya as Partial<T>;
}

/**
 * Kullanıcının KENDİ profil sayfasında istemci bileşenlerine geçilen alanlar.
 *
 * `profiles` tablosunda 27 kolon var ve sayfa `select("*")` yapıyordu; bu,
 * `paytr_card_token_encrypted` / `paytr_user_token_encrypted` gibi ŞİFRELİ ÖDEME
 * BİLGİLERİNİN RSC yüküne girip tarayıcıya (ve sayfa kaynağına, tarayıcı
 * önbelleğine, istemci hata raporlamasına) gitmesi demekti. Kendi satırı olsa
 * bile bu bilgilerin sunucudan çıkması için hiçbir sebep yok.
 *
 * Bugün canlıda 0 profilde token var — yani düzeltmenin şu anki etkisi sıfır,
 * ilk ödemeden ÖNCE kapatılmış bir kapı.
 *
 * ⚠️ Düz şablon dizesi BİLEREK — `.join()` literal tipi bozar (GenericStringError).
 */
export const PROFILE_SELF_FIELDS = `
  id, email, name, surname, role, profile_image, description,
  phone, whatsapp, website, created_at
`;

/** `PROFILE_SELF_FIELDS` ile birebir örtüşen tip. Kolon eklenirse ikisi de güncellenmeli. */
export type SelfProfile = Pick<
  Profile,
  | "id"
  | "email"
  | "name"
  | "surname"
  | "role"
  | "profile_image"
  | "description"
  | "phone"
  | "whatsapp"
  | "website"
  | "created_at"
>;
